import { z } from 'zod';

export interface UpdateGuideTargetsParams {
  updates: UpdateGuideTargetItemDto[];
  reasonForChange: string;
  updatedBy: string;
}

export interface GetGuideTargetsParams {
  yearMo: number;
  clientIds?: number[];
  guideIds?: string[];
  targetTypeKeys?: string[];
  serviceAreaKeys?: string[];
  sortBy?: string;
  sortDirection?: string;
}

/**
 * Revenue rollup for a single client + reporting month (when the list is filtered to one client).
 */
export interface GuideTargetsClientRevenueDto {
  clientId: number;
  yearMo: number;
  /** One entry per `guides.revenue_target` row for this client and `yearMo`. */
  byServiceArea: Array<{ serviceAreaKey: string; revenueTarget: number }>;
}

export interface GetGuideTargetsResult {
  items: GuideTargetPivotedRowDto[];
  count: number;
  targetTypeKeys: string[];
  serviceAreaKeys: string[];
  totalQueued: number;
  totalsByTargetType: GuideTargetTotalByTypeDto[];
  /**
   * Set when the request filters to exactly one `clientId`; otherwise `null`.
   */
  clientRevenue: GuideTargetsClientRevenueDto | null;
}

export interface GuideTargetRowDto {
  id: number;
  userId: string;
  clientId: number;
  yearMo: number;
  target: number;
  targetType: string;
  targetTypeKey: string;
  serviceArea: string | null;
  serviceAreaKey: string | null;
  createdSource: string | null;
  queuedMembersCount?: number | null;
  createdAt: number;
  updatedAt: number | null;
  updatedBy: { id: string; firstName: string; lastName: string } | null;
}

export interface GuideTargetHistoryDto {
  id?: number;
  guideTargetId: number;
  oldTarget: number;
  newTarget: number;
  reasonForChange: string | null;
  changedAt: number;
  changedBy: string | null;
  changedByUser: { id: string; firstName: string; lastName: string } | null;
}

export interface GuideTenureAndQueuedMembersDto {
  id: number;
  guideId: string;
  teamManagerId: string | null;
  customer: string;
  clientId: number;
  reemaServiceArea: string;
  reemaServiceAreaKey: string;
}

/**
 * Pivoted row: one per guide per client per service area, with all target types
 * as key-value pairs (targetTypeKey -> target value).
 * Queued fields: exact match on service area when present; otherwise sum/weighted
 * avg across all tenure rows for that guide+client+yearMo.
 */
export interface GuideTargetPivotedRowDto {
  /**
   * Per target type: creating user id when the cell was manually inserted; null when sourced from ETL/sync.
   */
  createdSourceByTargetTypeKey: Record<string, string | null>;
  userId: string;
  clientId: number;
  yearMo: number;
  serviceArea: string | null;
  serviceAreaKey: string | null;
  queuedMembersCount: number | null;
  avgQueuedMemberMonthsInProgram: number | null;
  targets: Record<string, number | 'NA'>;
  guideTargetIdsByTargetTypeKey: Record<string, number>;
  monthsTenured: number | null;
  startDate: string | null;
  teamManagerId: string | null;
  hasTargetsUpdatedByUser: boolean;
}

export interface GuideTargetTotalByTypeDto {
  clientId: number | null;
  serviceAreaKey: string | null;
  targetTypeKey: string;
  total: number;
}

export interface GuideTargetsAvailablePeriodsDto {
  yearMoValues: number[];
}

export interface PaginatedGuideTargetsDto {
  // One row per guide per client per service area; target types in `targets`
  items: GuideTargetPivotedRowDto[];
  count: number;
  targetTypeKeys: string[];
  serviceAreaKeys: string[];
  totalQueued: number;
  totalsByTargetType: GuideTargetTotalByTypeDto[];
  /**
   * Set when the request filters to exactly one `clientId`; otherwise `null`.
   */
  clientRevenue: GuideTargetsClientRevenueDto | null;
}

export const UpdateGuideTargetItemSchema = z
  .object({
    guideTargetId: z.number(),
    target: z.coerce.number().int().min(0),
  })
  .strict();

export type UpdateGuideTargetItemDto = z.infer<
  typeof UpdateGuideTargetItemSchema
>;

function plainTextFromHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/\u00a0/g, ' ')
    .trim();
}

/** True when the rich-text reason has visible content (empty Quill HTML is treated as empty). */
export function isGuideTargetReasonForChangeNonEmpty(html: string): boolean {
  return plainTextFromHtml(html).length > 0;
}

/** API / server: validates shape only; reason is enforced when updates actually change persisted targets. */
export const UpdateGuideTargetsRequestDtoSchema = z
  .object({
    updates: z.array(UpdateGuideTargetItemSchema),
    reasonForChange: z.string(),
  })
  .strict();

/**
 * Care portal form: require reason only when at least one target differs from `baselineTargets`
 * (same order as `updates`).
 */
export function createUpdateGuideTargetsFormSchema(
  baselineTargets: readonly number[]
) {
  return UpdateGuideTargetsRequestDtoSchema.superRefine((data, ctx) => {
    if (data.updates.length !== baselineTargets.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'updates must match baseline length',
        path: ['updates'],
      });
      return;
    }
    const hasTargetChange = data.updates.some(
      (u, i) => u.target !== baselineTargets[i]
    );
    if (!hasTargetChange) return;
    if (!isGuideTargetReasonForChangeNonEmpty(data.reasonForChange)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['reasonForChange'],
      });
    }
  });
}

export type UpdateGuideTargetsRequestDto = z.infer<
  typeof UpdateGuideTargetsRequestDtoSchema
>;

export const GuideTargetTypeSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    key: z.string(),
  })
  .strict();

export type GuideTargetTypeDto = z.infer<typeof GuideTargetTypeSchema>;

export const CreateGuideTargetSchema = z.array(
  z.object({
    id: z.number(),
    target: z.coerce.number().int().min(0),
  })
);

/** Create payload: one set of guide-target row fields plus a list of target types. */
export const CreateGuideTargetTypeDtoSchema = z
  .object({
    guideTargetTypes: CreateGuideTargetSchema,
    guideId: z.string(),
    clientId: z.number(),
    yearMo: z.number(),
    serviceAreaId: z.number().nullable().optional(),
  })
  .strict();

export type CreateGuideTargetTypeDto = z.infer<
  typeof CreateGuideTargetTypeDtoSchema
>;

const emptyGuideTargetCreateFieldToUndefined = (value: unknown): unknown => {
  if (value === '' || value === null) {
    return undefined;
  }
  return value;
};

/**
 * Care Portal: validates the “add targets” drawer before mapping to {@link CreateGuideTargetTypeDtoSchema}.
 */
export const GuideTargetsCreateFormSchema = z
  .object({
    clientId: z.preprocess(
      emptyGuideTargetCreateFieldToUndefined,
      z.number({ invalid_type_error: 'Client is required' }).int().positive()
    ),
    guideId: z.preprocess(
      emptyGuideTargetCreateFieldToUndefined,
      z.string({ invalid_type_error: 'Guide is required' }).min(1)
    ),
    serviceAreaId: z.preprocess(
      emptyGuideTargetCreateFieldToUndefined,
      z.number().int().positive().optional()
    ),
    guideTargetTypes: z
      .array(
        z.object({
          id: z.preprocess(
            emptyGuideTargetCreateFieldToUndefined,
            z
              .number({ invalid_type_error: 'Target type is required' })
              .int()
              .positive()
          ),
          target: z.coerce.number().int().min(0),
        })
      )
      .min(1, 'Add at least one target type row'),
  })
  .strict();

export type GuideTargetsCreateFormValues = z.infer<
  typeof GuideTargetsCreateFormSchema
>;

export const CreateUpdateDeleteGuideTargetsRequestDtoSchema = z.object({
  toAdd: CreateGuideTargetTypeDtoSchema.optional().nullable(),
  toUpdate: UpdateGuideTargetsRequestDtoSchema.optional().nullable(),
  toDelete: z.array(z.number()).optional().nullable().default([]),
  yearMo: z.number(),
});

export type CreateUpdateDeleteGuideTargetsRequestDto = z.infer<
  typeof CreateUpdateDeleteGuideTargetsRequestDtoSchema
>;

/**
 * Care Portal edit drawer: extends {@link CreateUpdateDeleteGuideTargetsRequestDtoSchema}
 * so that when any persisted target value differs from the current baseline (excluding rows
 * in `toDelete`), `toUpdate.reasonForChange` must contain visible rich-text content.
 *
 * Pass a getter (e.g. `() => baselineRef.current`) so react-hook-form can keep one stable
 * resolver while validation always sees the latest baseline after resets.
 *
 * @param getBaseline Same order as `toUpdate.updates`; typically from a ref updated when the drawer loads/resets.
 * @returns Zod schema for react-hook-form + zodResolver on the combined create/update/delete DTO.
 */
export function createGuideTargetsEditFormSchema(
  getBaseline: () => readonly number[]
) {
  return CreateUpdateDeleteGuideTargetsRequestDtoSchema.superRefine(
    (data, ctx) => {
      const baselineTargets = getBaseline();
      const toUpdate = data.toUpdate;
      if (toUpdate == null) {
        return;
      }
      const updates = toUpdate.updates ?? [];
      if (updates.length !== baselineTargets.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'updates must match baseline length',
          path: ['toUpdate', 'updates'],
        });
        return;
      }
      const deleteSet = new Set(data.toDelete ?? []);
      const hasPersistedTargetChange = updates.some((u, i) => {
        if (deleteSet.has(u.guideTargetId)) {
          return false;
        }
        return u.target !== baselineTargets[i];
      });
      if (!hasPersistedTargetChange) {
        return;
      }
      if (
        !isGuideTargetReasonForChangeNonEmpty(toUpdate.reasonForChange ?? '')
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: ['toUpdate', 'reasonForChange'],
        });
      }
    }
  );
}

export const CreateRevenueTargetDtoSchema = z.object({
  clientId: z.coerce.number().int(),
  serviceAreaKey: z.string(),
  yearMo: z.coerce.number(),
  revenueTarget: z.coerce.number().int().min(0),
  createdBy: z.string(),
});

export type RevenueTargetDto = z.infer<typeof CreateRevenueTargetDtoSchema>;

export const UpdateRevenueTargetSchema = CreateRevenueTargetDtoSchema.omit({
  createdBy: true,
}).extend({
  updatedBy: z.string().optional(),
});

export type UpdateRevenueTarget = z.infer<typeof UpdateRevenueTargetSchema>;
